import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { BlogBreadcrumb } from "@/components/seo/BlogBreadcrumb";
import { Footer } from "@/components/layout/Footer";
import { SEOContentBlock } from "@/components/seo/SEOContentBlock";
import { RelatedReads } from "@/components/seo/RelatedReads";
import { getRelatedPosts } from "@/content/blogPosts";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CalendarDays } from "lucide-react";
import heroImage from "@/assets/gallery/aerial-river-cabanas.jpg";
import cabanaImg from "@/assets/cabanas/cabana-riverside.jpg";
import campingImg from "@/assets/gallery/glamping-tent-exterior.jpg";
import tubingImg from "@/assets/gallery/friends-tubing.jpg";

const PUBLISH_DATE = "May 9, 2026";
const CANONICAL = "/blog/river-road-new-braunfels-guide";
const TITLE = "River Road New Braunfels — Cabanas & Tubing";
const DESCRIPTION =
  "The complete guide to River Road in New Braunfels: cabana rentals, tubing the Guadalupe River, glamping, and tent camping on Texas's most iconic river road.";

const FAQS = [
  {
    q: "Where is River Road in New Braunfels?",
    a: "River Road follows the Guadalupe River from just outside downtown New Braunfels up into the Texas Hill Country toward Sattler and Canyon Lake. It's the most iconic stretch of river road in Texas and home to many of the best tubing, cabana, and camping spots on the Guadalupe.",
  },
  {
    q: "Can you camp on River Road?",
    a: "Yes — camping on River Road is a long-standing Texas tradition. Son's Guadalupe offers tent camping near New Braunfels with direct river access plus glamping bell tents on the same stretch.",
  },
  {
    q: "Is River Road good for tubing the Guadalupe?",
    a: "It's the best stretch in Texas for family tubing. The river runs cool and spring-fed with calm pools and gentle rapids, and Son's properties along River Road let you launch and float right from your cabana.",
  },
  {
    q: "What's the easiest way to plan a River Road trip?",
    a: "Book a cabana rental on the Guadalupe River with Son's Guadalupe. Add tube rentals, kayaks, or an overnight glamping or tent camping stay and the whole River Road experience is set up for you in one place.",
  },
];

const schemaJson = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://sonsguadalupe.com/" },
        { "@type": "ListItem", position: 2, name: "Blog", item: "https://sonsguadalupe.com/blog" },
        { "@type": "ListItem", position: 3, name: "River Road New Braunfels — The Complete Guide to Cabanas, Camping & Tubing", item: `https://sonsguadalupe.com/blog/river-road-new-braunfels-guide` },
      ],
    },
    {
      "@type": "BlogPosting",
      headline: "River Road New Braunfels — Cabanas, Camping & Tubing Guide",
      description: DESCRIPTION,
      image: "https://sonsguadalupe.com/og-image.png",
      author: { "@type": "Organization", name: "Son's Guadalupe" },
      datePublished: "2026-05-09",
      mainEntityOfPage: { "@type": "WebPage", "@id": `https://sonsguadalupe.com${CANONICAL}` },
      keywords:
        "river road new braunfels, camping on river road, cabanas river road, tubing river road guadalupe, glamping new braunfels",
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    },
  ],
};

const BlogRiverRoadNewBraunfels = () => {
  const related = getRelatedPosts("river-road-new-braunfels-guide", 3);
  return (
    <div className="min-h-screen">
      <SEOHead title={TITLE} description={DESCRIPTION} canonical={CANONICAL} schemaJson={schemaJson} />
      <Header />
      <BlogBreadcrumb />
      <main className="pt-24 pb-16">
        <div className="relative h-[55vh] min-h-[400px]">
          <img fetchPriority="high" loading="eager" decoding="async" src={heroImage} alt="Aerial view of cabanas along River Road on the Guadalupe River near New Braunfels" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
            <div className="container-narrow mx-auto">
              <div className="flex items-center gap-2 text-white/80 text-sm mb-3">
                <CalendarDays className="w-4 h-4" /> {PUBLISH_DATE}
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-white max-w-4xl leading-tight">
                River Road New Braunfels — The Complete Guide to Cabanas, Camping & Tubing
              </h1>
            </div>
          </div>
        </div>

        <article className="section-padding bg-background">
          <div className="container-narrow mx-auto max-w-3xl px-4">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-primary hover:underline mb-8">
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>

            <div className="prose prose-lg max-w-none text-foreground space-y-6">
              <p className="text-xl leading-relaxed">
                If you've ever planned a Texas summer trip, you've heard of <strong>River Road in New Braunfels</strong>. It's the most iconic river road in the state — winding along the Guadalupe River from downtown New Braunfels into the Hill Country, lined with cabanas, campsites, and the best tubing in Texas. This guide covers everything: where River Road is, what to do there, and how to book a stay that takes care of all the logistics for you.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">Where Is River Road, Exactly?</h2>
              <p>
                River Road follows the Guadalupe River north out of New Braunfels toward Sattler and Canyon Lake. The stretch is famous for clear, spring-fed water, towering cypress trees, and a string of family-owned river properties — including several Son's Guadalupe locations. It's a 30-minute drive from San Antonio's north side and about 90 minutes from Austin.
              </p>

              <img src={cabanaImg} alt="Riverside cabana rental on River Road in New Braunfels" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">Cabana Rentals on River Road</h2>
              <p>
                <strong>Cabana rentals on the Guadalupe River</strong> are the easiest way to experience River Road. Each Son's cabana includes a shaded picnic table, direct river access, free parking, and room for six guests. Float, swim, grill, and head back to your private spot — no crowded outfitter parking lot required.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">Tubing the Guadalupe From River Road</h2>
              <p>
                River Road is the gold standard for <strong>tubing the Guadalupe River</strong>. Son's properties launch on calm, family-friendly stretches that mix slow pools with gentle rapids. Reserve tubes online, post up at your cabana, and float straight from your spot. It's the simplest tubing logistics on the river.
              </p>

              <img src={tubingImg} alt="Friends tubing the Guadalupe River along River Road in New Braunfels" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">Camping on River Road — Glamping & Tent Sites</h2>
              <p>
                <strong>Camping on River Road</strong> is a generations-deep Texas tradition. Son's offers two ways to do it: fully outfitted glamping bell tents with real beds and AC, or classic tent camping near New Braunfels with riverside sites, fire pits, and bathrooms close by. Wake up steps from the water and float before breakfast.
              </p>

              <img src={campingImg} alt="Glamping tent on River Road near the Guadalupe River" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">A Sample River Road Weekend</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Friday afternoon: arrive, check in to your cabana or glamping tent, swim, grill out</li>
                <li>Saturday: tube the Guadalupe River, lunch at the cabana, second short float</li>
                <li>Saturday night: campfire s'mores, stars, sleep to the sound of the river</li>
                <li>Sunday: morning kayak or coffee on the river, head home rested</li>
              </ul>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">Plan Your River Road Trip With Son's</h2>
              <p>
                Whether you want a single day at a cabana or a full weekend of glamping and tubing, Son's Guadalupe owns and operates several properties along River Road. One booking flow, one team, one of the most beautiful stretches of river in Texas.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">River Road FAQs</h2>
              <div className="space-y-6">
                {FAQS.map((f) => (
                  <div key={f.q}>
                    <h3 className="font-heading text-lg font-semibold text-foreground">{f.q}</h3>
                    <p className="mt-2">{f.a}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
                <Link to="/book-now"><Button variant="default" size="lg" className="text-lg px-10 py-6">Book a River Road Stay</Button></Link>
                <Link to="/property-map"><Button variant="outline" size="lg" className="text-lg px-10 py-6">See Properties</Button></Link>
              </div>
            </div>
          </div>
        </article>
        <RelatedReads posts={related} />

      </main>
      <SEOContentBlock
        eyebrow="More River Road Resources"
        heading="Cabanas, Tubing & Camping on the Guadalupe"
        paragraphs={[
          "Pair a River Road cabana rental on the Guadalupe River with tubing, glamping near New Braunfels, or tent camping on River Road. Son's Guadalupe makes family getaways near New Braunfels effortless.",
        ]}
        links={[
          { label: "Cabanas", to: "/cabanas" },
          { label: "Tubing", to: "/tubing" },
          { label: "Glamping", to: "/glamping" },
          { label: "Tent Camping", to: "/tent-camping-new-braunfels" },
        ]}
      />
      <Footer />
    </div>
  );
};

export default BlogRiverRoadNewBraunfels;
